"use client";

import { getCribnoshVideoLabel } from "@shared/contentLabels";
import { ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useState } from "react";

type CreatorMode = "creator" | "studio";

const modes: Record<
  CreatorMode,
  {
    label: string;
    title: string;
    description: string;
    primaryLabel: string;
    secondaryLabel: string;
    primaryHref: string;
    previewSrc: string;
    previewAlt: string;
    iconSrc?: string;
    overlayClassName?: string;
    overlayRotate?: number;
    previewContain?: boolean;
    appCtaHref?: string;
    appCtaLabel?: string;
    appCtaIconSrc?: string;
    highlights: string[];
  }
> = {
  creator: {
    label: "For cooks",
    title: "Bring your signature dishes to the people already craving them.",
    description:
      "Start with the food that tells your story. Build your page, share kitchen moments, and open orders when the time feels right.",
    primaryLabel: "Use web",
    secondaryLabel: "See how it works",
    primaryHref: "/become-a-food-creator/apply?type=food-creator",
    previewSrc: "/app-downloads/creator-preview.png",
    previewAlt: "CribNosh Creator promotional artwork showing a food creator cooking.",
    iconSrc: "/app-downloads/creator.png",
    overlayClassName: "right-[-4%] top-[12%] w-[84px] sm:w-[98px]",
    overlayRotate: 11,
    previewContain: true,
    appCtaHref: "/food-creator",
    appCtaLabel: "Use Food Creator App",
    appCtaIconSrc: "/app-downloads/creator.png",
    highlights: [`Post dishes and ${getCribnoshVideoLabel({ plural: true, lowercase: true })}`, "Build a following locally", "Open orders when ready"],
  },
  studio: {
    label: "For kitchens",
    title: "Put your kitchen to work for the next wave of food creators.",
    description:
      "Offer trusted kitchen time to verified local creators and keep your space active beyond your own peak hours.",
    primaryLabel: "List your kitchen",
    secondaryLabel: "See how it works",
    primaryHref: "/become-a-food-creator/apply?type=studio-owner",
    previewSrc: "/backgrounds/masonry-1.jpg",
    previewAlt: "Kitchen studio space for food creators",
    highlights: ["List available kitchen time", "Host verified creators", "Back local food businesses"],
  },
};

export function FoodCreatorHero() {
  const router = useRouter();
  const [activeMode, setActiveMode] = useState<CreatorMode>("creator");
  const active = modes[activeMode];
  const appCtaHref = active.appCtaHref;
  const appCtaLabel = active.appCtaLabel;
  const appCtaIconSrc = active.appCtaIconSrc;

  return (
    <section
      id="overview"
      className="relative overflow-hidden bg-[#082419] text-white"
      data-section-theme="light"
    >
      <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(8,36,25,0.96)_0%,rgba(8,36,25,0.92)_55%,rgba(9,44,28,0.88)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,95,71,0.5),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(10,92,54,0.3),transparent_30%)]" />
      <div className="relative mx-auto grid min-h-[78svh] max-w-7xl items-center px-6 pb-14 pt-32 sm:px-8 sm:pt-36 lg:px-10 lg:pb-16 lg:pt-40">
        <div className="mb-8 flex flex-wrap items-center gap-5">
            {(["creator", "studio"] as CreatorMode[]).map((mode) => {
              const isActive = mode === activeMode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setActiveMode(mode)}
                  className={`relative pb-2 font-satoshi text-sm font-semibold transition ${
                    isActive
                      ? "text-white"
                      : "text-white/56 hover:text-white"
                  }`}
                >
                  {modes[mode].label}
                  <span
                    className={`absolute inset-x-0 -bottom-px h-0.5 rounded-full transition ${
                      isActive ? "bg-[#FF5F47]" : "bg-transparent"
                    }`}
                  />
                </button>
              );
            })}
        </div>

        <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(340px,0.9fr)] lg:gap-14">
            <div className="max-w-2xl">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeMode}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.28, ease: "easeOut" }}
                >
                  <h1 className="max-w-3xl font-asgard text-5xl leading-[0.94] tracking-[-0.04em] text-white sm:text-6xl lg:text-7xl">
                    {active.title}
                  </h1>
                  <p className="mt-6 max-w-2xl font-satoshi text-lg leading-8 text-white/82 sm:text-xl">
                    {active.description}
                  </p>

                  <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <button
                      type="button"
                      onClick={() => router.push(active.primaryHref)}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FF5F47] px-6 py-3.5 font-satoshi text-base font-bold text-white transition hover:bg-[#ff715c] sm:w-[220px]"
                    >
                      {active.primaryLabel}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                    {appCtaHref && appCtaLabel && appCtaIconSrc ? (
                      <button
                        type="button"
                        onClick={() => router.push(appCtaHref)}
                        className="inline-flex w-full max-w-full items-center justify-center gap-3 overflow-hidden rounded-full border border-white/16 bg-[#0A5C36]/28 px-5 py-3.5 font-satoshi text-base font-semibold text-white transition hover:bg-[#0A5C36]/36 sm:w-auto sm:min-w-[250px]"
                      >
                        <span className="relative block h-9 w-9 shrink-0 overflow-hidden rounded-[10px] bg-white ring-1 ring-white/18">
                          <Image
                            src={appCtaIconSrc}
                            alt=""
                            fill
                            aria-hidden="true"
                            className="object-cover"
                            sizes="36px"
                          />
                        </span>
                        <span className="min-w-0 truncate">{appCtaLabel}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => router.push("#start")}
                        className="inline-flex items-center justify-center rounded-full border border-white/16 bg-[#0A5C36]/28 px-6 py-3.5 font-satoshi text-base font-semibold text-white transition hover:bg-[#0A5C36]/36"
                      >
                        {active.secondaryLabel}
                      </button>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="mt-7 flex flex-wrap gap-x-4 gap-y-2">
                {active.highlights.map((item) => (
                  <p
                    key={item}
                    className="font-satoshi text-sm font-medium text-white/78"
                  >
                    <span className="mr-2 text-[#FF5F47]">•</span>
                    {item}
                  </p>
                ))}
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={`${activeMode}-media`}
                initial={{ opacity: 0, y: 70, scale: 0.94, rotate: -4 }}
                animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, y: 24, scale: 0.96, rotate: -2 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="relative"
              >
                <div className="mx-auto w-full max-w-[420px]">
                  <div className="relative aspect-[437/581] w-full">
                    <Image
                      src={active.previewSrc}
                      alt={active.previewAlt}
                      fill
                      className={
                        active.previewContain
                          ? "object-contain object-center"
                          : "rounded-[28px] object-cover"
                      }
                      sizes="(max-width: 1024px) 100vw, 420px"
                      priority
                    />

                    {active.iconSrc ? (
                      <motion.div
                        key={`${activeMode}-overlay`}
                        initial={{
                          opacity: 0,
                          scale: 0.82,
                          rotate: (active.overlayRotate || 0) - 6,
                          y: 10,
                        }}
                        animate={{
                          opacity: 1,
                          scale: 1,
                          rotate: active.overlayRotate || 0,
                          y: 0,
                        }}
                        transition={{ duration: 0.38, ease: "easeOut" }}
                        className={`pointer-events-none absolute ${active.overlayClassName || ""}`}
                      >
                        <div className="relative aspect-square w-full overflow-hidden rounded-[18px] ring-1 ring-white/10">
                          <Image
                            src={active.iconSrc}
                            alt=""
                            fill
                            aria-hidden="true"
                            className="object-cover"
                            sizes="108px"
                          />
                        </div>
                      </motion.div>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
